import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Globe, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { usePageMeta } from "@/hooks/usePageMeta";

interface Analysis {
  id: string;
  url: string;
  results: unknown;
  created_at: string;
}

export default function AnalysesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  usePageMeta({ title: "My Analyses — Unranked" });
  const [analyses, setAnalyses] = useState<Analysis[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      supabase
        .from("analyses")
        .select("id, url, results, created_at")
        .order("created_at", { ascending: false })
        .then(({ data }) => {
          setAnalyses((data as Analysis[]) ?? []);
          setLoading(false);
        });
    }
  }, [user]);

  const handleClick = (analysis: Analysis) => {
    const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(analysis.results))));
    const email = user?.email ?? "";
    navigate(`/report?data=${encodeURIComponent(encoded)}&email=${encodeURIComponent(email)}`);
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-foreground font-display">Previous Analyses</h1>
        <p className="text-muted-foreground text-sm mt-1">View and revisit your past SEO analyses.</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : analyses.length > 0 ? (
        <div className="space-y-2">
          {analyses.map((analysis) => (
            <button
              key={analysis.id}
              onClick={() => handleClick(analysis)}
              className="w-full flex items-center gap-4 px-4 py-3 rounded-lg border border-border bg-card text-left hover:bg-muted/50 transition-colors"
            >
              <Globe className="h-5 w-5 text-muted-foreground shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate text-foreground">{analysis.url}</p>
                <p className="text-xs text-muted-foreground">
                  {format(new Date(analysis.created_at), "MMM d, yyyy 'at' h:mm a")}
                </p>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-card p-12 text-center">
          <p className="text-muted-foreground">No analyses yet. Run your first analysis to see it here.</p>
        </div>
      )}
    </div>
  );
}
